<template lang="pug">
  .b-notifications(:class="{ tile }" v-if="itemsRaw.length")
    ._header(v-if="!tile")
      div
        BaseTabs2(:items="tabs" v-model="filters.tab")
      ._project-filter
        BaseSelect(:options="projects" v-model="filters.project" notSelectedLabel="Все проекты" label="Все проекты")
      ._disable-sound
        Icon(name="no-sound" :class="{ active: soundDisabled }" pointer @click.native="toggleSound")
      ._spacer
      router-link._settings(:to="{ name: 'notification-settings' }")
        Icon(name="setting" pointer)
    ._list
      ._item(v-for="item in itemsFiltered" :class="{ new: !item.isViewed }" :key="item.id")
        NotificationComponentSelector._title(:item="item" type="title")
        ._project {{ item.info.projectName || item.info.projectId }}
        NotificationComponentSelector._icon(:item="item" type="icon")
        UserTile._initiator(:user="item.initiator")
          ._datetime {{ formatDate(item.createdAt) }}
        Icon._delete(name="close3" pointer @click.native="deleteNotification(item)")
  .no-notifications(v-else)
    img(src="/img/ghost.png")
    | Нет уведомлений
</template>

<script>
import utils from '@/utils/common';
import modelUserCustomSettings from '@/model/userCustomSettings';

import FormBlock from '@/components/FormBlock.vue';
import BaseSelect from '@/components/BaseSelect.vue';
import NotificationComponentSelector from '@/components/notification/NotificationComponentSelector.vue';
import UserTile from '@/components/UserTile.vue';
import form from '@/utils/form';
import BaseTabs2 from '@/components/BaseTabs2.vue';

export default {
  name: 'NotificationList',
  props: {
    tile: Boolean,
  },
  components: {
    BaseTabs2,
    FormBlock,
    BaseSelect,
    NotificationComponentSelector,
    UserTile,
  },
  data() {
    return {
      notViewedItems: new Set(),
      filters: {
        project: null,
        tab: 'all',
      },
    };
  },
  mounted() {
    if (this.tile) return;

    if (this.unreadItemsLength) {
      this.filters.tab = 'unread';
    }
  },
  watch: {
    itemsRaw: {
      immediate: true,
      handler(val) {
        if (!val || this.tile) return;

        const notViewedItems = val.filter((i) => !i.isViewed);

        notViewedItems.forEach((i) => {
          this.notViewedItems.add(i.id);
          this.$store.dispatch('notification/view', i);
        });
      },
    },
  },
  computed: {
    tabs() {
      return [
        { name: 'all', label: 'Все' },
        { name: 'unread', label: 'Непрочитанные', count: this.unreadItemsLength },
      ];
    },
    notificationSettings() { return this.$store.state.auth.user?.customSettings?.notifications ?? {}; },
    soundDisabled() { return this.notificationSettings?.soundDisabled === true; },
    itemsRaw() { return this.$store.state.notification.items; },
    unreadItemsLength() { return this.itemsMarkedByView.filter(i => !i.isViewed).length; },
    itemsMarkedByView() {
      return this.itemsRaw
        .map((i) => ({
          ...i,
          isViewed: this.notViewedItems.has(i.id) ? false : i.isViewed,
        }));
    },
    itemsFiltered() {
      return this.itemsMarkedByView
        .filter((i) => {
          const isSuitableByProject = !this.filters.project || this.filters.project === i.info.projectId?.toString();
          const isSuitableByView = this.filters.tab === 'all' || !i.isViewed;
          return isSuitableByProject && isSuitableByView;
        });
    },
    projects() {
      return form.makeSelectOptionsFromObject(
        this.itemsRaw.reduce((acc, val) => {
          // eslint-disable-next-line prefer-const
          let { projectId, projectName } = val.info;

          if (!projectName) projectName = projectId; // todo remove

          if (!acc[projectId]) {
            acc[projectId] = projectName;
          }

          return acc;
        }, {}),
      );
    },
  },
  methods: {
    formatDate(date) {
      return date ? utils.convertDateToString(new Date(date), 'short', true) : '';
    },
    async deleteNotification(item) {
      if (await this.$confirm('Удалить уведомление?')) {
        this.$store.dispatch('notification/delete', item);
      }
    },
    toggleSound() {
      const soundDisabled = !this.soundDisabled;
      const { notificationSettings } = this;
      modelUserCustomSettings.set({ notifications: { ...notificationSettings, soundDisabled } })
        .then(() => { this.$push({ text: `Звуковые оповещения ${soundDisabled ? 'выключены' : 'включены'}` }); });
    },
  },
};
</script>

<style scoped lang="scss">
  .notifications {
    padding-top: 0 !important;

    &__header {
      display: grid;
      grid-template-columns: 1fr 250px 40px 250px 35px;
      gap: 25px;
      align-items: center;
      margin-bottom: 15px;
    }

    &__disable-sound {
      align-self: center;
      justify-self: center;

      svg {
        height: 20px;
        fill: #99A3C5;
        &.active {
          fill: var(--color-accent);
        }
      }
    }

    &__spacer { justify-self: center }

    &__settings {
      line-height: 0;

      svg {
        fill: #99A3C5;
        width: 26px;
        height: 26px;
      }
    }

    &__list {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    &__item {
      padding: 25px;
      display: grid;
      gap: 25px;
      grid-template-columns: 1fr 230px 40px 250px 10px;
      border-radius: 6px;

      &:nth-child(odd) {
        background: #F5F7F9;
      }

      &.new {
        background: #EFF7FF;
        border-radius: 6px;
      }

      &:first-child, &.new, &.new + & {
        border-top: none !important;
      }
    }

    &__title::v-deep {
      color: var(--color-dark-gray);
      grid-row: 1 / 2 span;

      a {
        color: var(--color-accent) !important;
      }
    }

    &__icon {
      justify-self: center;
      align-self: center;
    }

    &__datetime {
      font-weight: normal;
      font-size: 12px;
      line-height: 15px;
      text-align: center;
      color: #788CA1;
      align-self: center;
    }

    &__delete {
      align-self: center;
    }

    @media (max-width: 1249px) {
      &__header {
        grid-template-columns: 1fr 20px 20px;
      }

      &__spacer {
        display: none;
      }

      &__project-filter {
        grid-row: 2;
        grid-column: 1 / 3 span;
      }

      &:not(.tile) &__item {
        display: block;
        position: relative;
        padding: 17px 45px 16px;
        background: #F5F7F9;
      }

      &:not(.tile) &__icon {
        position: absolute;
        top: 16px;
        left: 15px;
      }

      &:not(.tile) &__delete {
        position: absolute;
        top: 24px;
        right: 16px;
      }

      &__project {
        font-size: 14px;
        line-height: 18px;
        margin: 22px 0 24px;
      }
    }

    &.tile &__list {
      max-height: 430px;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 0 27px 0 25px;
      gap: 1px;

      &::-webkit-scrollbar { width: 5px; }
      &::-webkit-scrollbar-track { background: #F1F1F1; }
      &::-webkit-scrollbar-thumb { background: #C1C1C1; }
    }

    &.tile &__item {
      grid-template-columns: 20px 1fr 250px 10px;
      gap: 10px;
      align-items: center;
      justify-items: start;
      padding: 13px 0;
      border-radius: 0;
      border-top: 1px solid #D1D9F0;

      &:last-child {
        border-bottom: none;
      }

      &:last-child:not(.new) {
        border-bottom: 1px solid #D1D9F0;
      }

      &.new {
        background: #FFFFFF;
        position: relative;

        &:before, &:after {
          content: '';
          display: block;
          position: absolute;
          background: #FFFFFF;
          width: 25px;
          height: 100%;
          top: 0;
          bottom: 0;
        }

        &:before {
          right: 100%;
          border-radius: 8px 0 0 8px;
        }

        &:after {
          left: 100%;
          border-radius: 0 8px 8px 0;
        }
      }
    }

    &.tile &__title {
      font-weight: 500;
      font-size: 12px;
      line-height: 15px;
      grid-column: 2;
      grid-row: 1 / 1 span;
    }

    &.tile &__project {
      display: none;
    }

    &.tile &__icon {
      grid-column: 1;
    }

    &.tile &__initiator {
      display: block;
      &::v-deep {
        .name {
          font-size: 12px !important;
        }
        .avatar {
          width: 40px;
          height: 40px;
        }
      }
    }

    @media (max-width: 992px) {
      &.tile &__item {
        grid-template-columns: 20px 1fr 10px;
      }

      &.tile &__initiator {
        grid-row: 2;
        grid-column: 2;
      }
    }
  }

  .no-notifications {
    padding: 60px;
    border: 1px solid #EBEFF2;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-weight: 400;
    font-size: 14px;
    line-height: 21px;
    text-align: center;
    color: #99A3C5;

    img {
      margin-bottom: 15px;
    }
  }
</style>
